import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from '../index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { addUserCertificateEntity, editStaffMember, editStaffMemberEntity, getStaffMemberById } from "../../../../redux/features/staff/staffService";
import { showToast } from "../../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import Dropdown from "../../../../components/Dropdown/Dropdown";
import { getActiveRoles } from "../../../../redux/features/role/roleService";
import { selectActiveRoles } from "../../../../redux/features/role/roleSlice";
import { selectActiveCertificateList } from "../../../../redux/features/certificateEntity/certificateEntitySlice";
import { getActiveCertificateEntities } from "../../../../redux/features/certificateEntity/certificateEntityService";
import { useParams } from "react-router-dom";
import Modal from "../../../../components/Modal";
import { faCircleXmark } from "@fortawesome/free-solid-svg-icons";

const EditStaff = () => {

    const { id } = useParams();
    const dispatch = useDispatch();
    const entityDropdownRef = useRef(null);

    const { isEditingStaffMember } = useSelector((state) => state.staff.editedStaffMember);
    const { isStaffMemberLoading, isStaffMemberSuccess, staffMember } = useSelector((state) => state.staff.staffMemberDetails);
    const { roles } = useSelector(selectActiveRoles);
    const { status, data: activeCertificateEntities } = useSelector(selectActiveCertificateList);

    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone_number: '',
    });

    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedStaffMember, setSelectedStaffMember] = useState(null);
    const [initialStaffMember, setInitialStaffMember] = useState(null);
    const [editErrorMsgs, setEditErrorMsgs] = useState({
        name: '',
        staffMember_code: ''
    });
    const [selectedRole, setSelectedRole] = useState({
        id: '',
        name: ''
    });

    const [disabledEditBtn, setEditBtnDisable] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [entityError, setEntityError] = useState('');

    useEffect(() => {
        if (id) {
            dispatch(getStaffMemberById({ id }));
        }
    }, [id]);

    useEffect(() => {
        if (isStaffMemberSuccess) {
            setSelectedStaffMember(staffMember);
            setInitialStaffMember(staffMember);
            const activeEntities = staffMember?.user_certificate_entities?.filter(
                (entity) => entity?.status === true
            );

            setSelectedEntities(activeEntities);
            setRoleEtityCount(staffMember?.role?.entity_count);
        }
    }, [isStaffMemberSuccess]);

    useEffect(() => {
        dispatch(getActiveRoles());
        dispatch(getActiveCertificateEntities());
    }, [dispatch]);

    const [roleEntityCount, setRoleEtityCount] = useState(0);

    // Edit staffMember
    const handleSelectedStaffMemberChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null);
        setSelectedStaffMember((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEditErrorMsgs((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const isValidEditStaffMember = () => {
        const errors = {};
        const trimmedName = selectedStaffMember.full_name.trim();

        if (!trimmedName) {
            errors.full_name = "Name is required.";
        }
        if (!selectedStaffMember.phone_number) {
            errors.phone_number = "Phone Number is required.";
        }
        if (Object.keys(errors).length > 0) {
            setEditErrorMsgs(errors);
            return false;
        } else {
            return true;
        }
    };

    function getChangedValues(selectedData) {
        const changedValues = {};
        for (const key in selectedData) {
            if (
                key !== 'id' &&
                selectedData.hasOwnProperty(key) &&
                selectedData[key] !== initialStaffMember[key]
            ) {
                changedValues[key] = selectedData[key];
            }

            if (key === 'id') {
                changedValues[key] = selectedData[key];
            }
        }
        return changedValues;
    }

    const editStaffMemberDetails = async () => {
        if (isValidEditStaffMember(selectedStaffMember)) {
            const data = getChangedValues(selectedStaffMember);
            const hasChanges = Object.keys(data).some(key => key !== 'id');
            if (!hasChanges) {
                setErrorMsg('There are no changes to update.');
            } else {
                setEditBtnDisable(true);
                try {
                    const response = await dispatch(editStaffMember(data)).unwrap();
                    dispatch(getStaffMemberById({ id }));
                    setEditBtnDisable(false);
                    setModalOpen(false);
                    showToast('success', response.message);
                }
                catch (error) {
                    setEditBtnDisable(false);
                    showToast('error', error.message);
                };
            }
        }
    };
    // Certificate entities
    const [selectedEntity, setSelectedEntity] = useState({
        id: '',
        name: ''
    });
    const [selectedEntities, setSelectedEntities] = useState([]);
    const [disabledEntityCreate, setDisableEntityCreate] = useState(false);

    const handleSelectEntity = (entity) => {
        if (selectedEntities.length >= roleEntityCount) {
            setEntityError('Maximum entity count is reached.');
            return
        }
        setSelectedEntity({
            id: entity?.id,
            name: entity?.name
        });
    };

    const insertEntities = async () => {
        try {
            setDisableEntityCreate(true);
            const payload = {
                create_user_certificate_entities: [{
                    user_id: id,
                    certificate_entity_id: selectedEntity.id,
                }]
            };
            const response = await dispatch(addUserCertificateEntity(payload)).unwrap();
            showToast('success', response.message);
            dispatch(getStaffMemberById({ id }));
            setSelectedEntity({
                id: '',
                name: ''
            });
            if (entityDropdownRef.current) {
                entityDropdownRef.current.clearSelection();
            }
            setDisableEntityCreate(false);
        } catch (error) {
            showToast('error', error.message);
            setDisableEntityCreate(false);
        }
    }

    // Remove entity
    const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
    const [selectedStaffMemberEntityId, setSelectedStaffMemberEntityId] = useState(null);

    const closDeactivationModal = () => setDeactivationModalOpen(false);

    const handleRejectYes = async () => {
        setEntityError('');
        setDeactivationModalOpen(false);
        try {
            const response = await dispatch(editStaffMemberEntity({ id: selectedStaffMemberEntityId, status: false })).unwrap();
            setSelectedStaffMemberEntityId(null);
            dispatch(getStaffMemberById({ id }));
            showToast('success', response.message);
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Edit Staff Member</h1>
                <div className={styles.formContainer}>
                    <form
                        className={styles.coordinatorForm}
                        noValidate
                    >
                        <div className={`form-input-row md-col`}>
                            <div className={`form-input-group`}>
                                <label htmlFor='full_name' className={`form-label`}>
                                    Name<span className={`required-star`}>*</span>
                                </label>
                                <input
                                    type='text'
                                    id='full_name'
                                    name='full_name'
                                    value={selectedStaffMember?.full_name || ''}
                                    onChange={handleSelectedStaffMemberChange}
                                    placeholder='Enter StaffMember Name'
                                    className={`form-input-default`}
                                />
                                {editErrorMsgs.full_name && (
                                    <p className={`text-danger`}>{editErrorMsgs.full_name}</p>
                                )}
                            </div>
                        </div>
                        <div className={`form-input-row md-col`}>
                            <div className={`form-input-group`}>
                                <label htmlFor='full_name' className={`form-label`}>
                                    Email<span className={`required-star`}>*</span>
                                </label>
                                <input
                                    type='text'
                                    id='email'
                                    name='email'
                                    value={selectedStaffMember?.email || ''}
                                    disabled
                                    placeholder='Enter Email'
                                    className={`form-input-default`}
                                />
                                {editErrorMsgs.email && (
                                    <p className={`text-danger`}>{editErrorMsgs.email}</p>
                                )}
                            </div>
                        </div>
                        <div className={`form-input-row md-col`}>
                            <div className={`form-input-group`}>
                                <label htmlFor='full_name' className={`form-label`}>
                                    Phone Number<span className={`required-star`}>*</span>
                                </label>
                                <input
                                    type='text'
                                    id='phone_number'
                                    name='phone_number'
                                    value={selectedStaffMember?.phone_number || ''}
                                    onChange={handleSelectedStaffMemberChange}
                                    placeholder='Enter Phone Number'
                                    className={`form-input-default`}
                                />
                                {editErrorMsgs.phone_number && (
                                    <p className={`text-danger`}>{editErrorMsgs.phone_number}</p>
                                )}
                                {errorMsg && (
                                    <p className={`text-danger`}>{errorMsg}</p>
                                )}
                            </div>
                        </div>
                        <button
                            className={`btn-primary`}
                            type='button'
                            disabled={disabledEditBtn}
                            onClick={editStaffMemberDetails}
                        >
                            {isEditingStaffMember ? <BeatLoader color='#ffffff' /> : `Save`}
                        </button>
                    </form>
                </div>
            </div>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Edit Staff Member's Entities</h1>
                <div className={styles.formContainer}>
                    <form
                        className={styles.coordinatorForm}
                        noValidate
                    >
                        <div className={`form-input-row ${styles.addRow}`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Certificate Entity<span className={`required-star`}>*</span>
                                    </label>
                                    <Dropdown
                                        ref={entityDropdownRef}
                                        options={activeCertificateEntities}
                                        placeholder="Select an Entity"
                                        onSelect={handleSelectEntity}
                                        value={selectedEntity.name}
                                    />
                                </div>
                            </div>
                            <button className={`btn-primary`}
                                type='button' onClick={insertEntities}
                                disabled={disabledEntityCreate}>
                                {disabledEntityCreate ? <BeatLoader color='#ffffff' /> : `Add`}</button>
                        </div>
                        <div className={styles.selectedEntitiesWrapper}>
                            {selectedEntities
                                ?.map((entity) => (
                                    <div key={entity.certificate_entity_id} className={styles.entityCard}>
                                        <p>{entity?.certificate_entity?.name}</p>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedStaffMemberEntityId(entity.id);
                                                setDeactivationModalOpen(true);
                                            }}
                                            className={styles.removeBtn}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                        </div>
                                    {entityError && (
                                        <p className={`text-danger`}>{entityError}</p>
                                    )}
                    </form>
                </div>
            </div>
            {isDeactivationModalOpen && (
                <Modal
                    title='Remove Entity Access'
                    onClose={closDeactivationModal}
                    onYes={handleRejectYes}
                    isInput={false}
                    iconName={faCircleXmark}
                    bodyText={`Are you sure you want to remove access?`}
                    colorType={`var(--danger)`}
                />
            )}
        </div>
    );
};

export default EditStaff;